/*
Purpose: Main landing dashboard showing user shortcuts and recent items.
Audience: Logged-in users.
What it does:
- Aggregates tabs (Your Cases, Recently Viewed, User Activity, Add Case)
Interplay:
- Uses UniversalSidebar for navigation and tab components; links to casefiles and evidence
*/
import React, { useState, useEffect } from 'react';
import { db } from '../../firebase';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import UniversalSidebar from '../navigation/UniversalSidebar';
import { getDashboardSidebarItems } from '../navigation/sidebarConfigs';
import useAutoExpandSidebar from '../../hooks/useAutoExpandSidebar';
import { GlobalResultsModal } from '../modals/results/wrappers/CleanResultsModals';
import YourCasesTab from './YourCasesTab';
import AddCaseTab from './AddCaseTab';
import UserActivityTab from './UserActivityTab';
import RecentlyViewedTab from './RecentlyViewedTab';
import '../../styles/Dashboard.css';
import '../../styles/DashboardTabs.css';
import '../../styles/animations.css';

const Dashboard = ({user, sidebarOpen, onSidebarToggle}) => {
  const [casefiles, setCasefiles] = useState([]);
  const [userEvidence, setUserEvidence] = useState([]);
  const [allEvidence, setAllEvidence] = useState([]);
  const [activeTab, setActiveTab] = useState('your-cases');
  const [isGlobalResultsOpen, setIsGlobalResultsOpen] = useState(false);

  // Use auto-expand sidebar hook for the dashboard sidebar
  const {
    isOpen: isDashboardSidebarOpen,
    shouldAutoExpand: dashboardShouldAutoExpand,
    sidebarMode: dashboardSidebarMode,
    handleToggle: handleDashboardSidebarToggle
  } = useAutoExpandSidebar(sidebarOpen);

  // Sync external sidebar state with internal hook
  const handleSidebarToggle = (newState) => {
    if (onSidebarToggle) {
      onSidebarToggle(newState);
    }
    handleDashboardSidebarToggle(newState);
  };

  // Fetch user's casefiles
  useEffect(() => {
    if (!user) {
      return;
    }
    const q = query(
      collection(db, 'casefiles'),
      where('createdBy', '==', user.uid),
    );
    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      const files = [];
      querySnapshot.forEach((doc) => {
        files.push({id: doc.id, ...doc.data()});
      });
      setCasefiles(files);
    });
    return () => unsubscribe();
  }, [user]);

  // Fetch evidence uploaded by the user
  useEffect(() => {
    if (!user) return;
    
    // Temporary: Use simple collection query while indexes are building
    const q = query(
      collection(db, 'evidence'),
      where('createdBy', '==', user.uid)
    );
    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      const evidence = [];
      querySnapshot.forEach((doc) => {
        evidence.push({id: doc.id, ...doc.data()});
      });
      setUserEvidence(evidence);
    });
    return () => unsubscribe();
  }, [user]);

  // Fetch all evidence for user's casefiles (to show activity on their cases)
  useEffect(() => {
    if (!user || casefiles.length === 0) return;

    const casefileIds = casefiles.map(cf => cf.id);
    if (casefileIds.length === 0) return;

    // Temporary: Use simple collection query while indexes are building
    const q = query(
      collection(db, 'evidence'),
      where('casefileId', 'in', casefileIds)
    );
    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      const evidence = [];
      querySnapshot.forEach((doc) => {
        evidence.push({id: doc.id, ...doc.data()});
      });
      setAllEvidence(evidence);
    });
    return () => unsubscribe();
  }, [user, casefiles]);

  const renderTabContent = () => {
    switch (activeTab) {
      case 'your-cases':
        return <YourCasesTab user={user} casefiles={casefiles} allEvidence={allEvidence} />;
      case 'add-case':
        return <AddCaseTab user={user} onTabSwitch={setActiveTab} />;
      case 'user-activity':
        return <UserActivityTab casefiles={casefiles} userEvidence={userEvidence} allEvidence={allEvidence} />;
      case 'recently-viewed':
        return <RecentlyViewedTab />;
      default:
        return <YourCasesTab user={user} casefiles={casefiles} allEvidence={allEvidence} />;
    }
  };

  // Open global results modal showing all user's analysis orders
  const openLatestResults = () => {
    console.log('🌍 Dashboard: Opening global results modal');
    setIsGlobalResultsOpen(true);
  };

  // Debug: Track when global results modal state changes
  useEffect(() => {
    console.log('🌍 Dashboard: Global results modal state changed:', {
      isGlobalResultsOpen,
      timestamp: new Date().toISOString()
    });
  }, [isGlobalResultsOpen]);

  const sidebarItems = getDashboardSidebarItems(activeTab, setActiveTab, openLatestResults);

  return (
    <div className={`dashboard-container ${dashboardShouldAutoExpand ? 'content-with-auto-sidebar' : ''}`}>
      <UniversalSidebar 
        isOpen={isDashboardSidebarOpen}
        onToggle={handleSidebarToggle}
        items={sidebarItems}
        activeItem={activeTab}
        title="Dashboard"
        subtitle="Your Personal Hub"
        sidebarMode={dashboardSidebarMode}
        shouldAutoExpand={dashboardShouldAutoExpand}
      />
      {renderTabContent()}
      
      {/* Global Results Modal */}
      <GlobalResultsModal
        isOpen={isGlobalResultsOpen}
        onClose={() => {
          console.log('🌍 Dashboard: Global results modal onClose called');
          setIsGlobalResultsOpen(false);
        }}
        userId={user?.uid}
      />
    </div>
  );
};

export default Dashboard; 